import React, { useEffect, useMemo, useState, useRef } from 'react';
import { BarChart3Icon, UserCheckIcon, UsersIcon, XIcon, CheckIcon, ChevronRightIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Select } from '../ui/Select';
import { Appointment } from '../../types';
import { getToday } from '../../utils/helpers';
import { exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { exportDashboardChartDataToExcel } from '../../utils/excelExport';
import { useAuth } from '../../contexts/AuthContext';
interface AppointmentsByUserProps {
  appointments: Appointment[];
}
export function DashboardAppointmentsByUser({
  appointments
}: AppointmentsByUserProps) {
  const {
    user
  } = useAuth();
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canDownloadPdf = user?.role === 'administrador' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [aptByUserGroupBy, setAptByUserGroupBy] = useState<'day' | 'month' | 'year'>('month');
  const [aptDateFrom, setAptDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [aptDateTo, setAptDateTo] = useState<string>(getToday());
  const [aptSelectedUsers, setAptSelectedUsers] = useState<string[]>([]);
  const [aptUserDropdownOpen, setAptUserDropdownOpen] = useState(false);
  const [aptStatusFilter, setAptStatusFilter] = useState<string>('completada');
  const aptUserDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (aptUserDropdownRef.current && !aptUserDropdownRef.current.contains(e.target as Node)) {
        setAptUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const USER_COLORS = ['#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
  const stats = useMemo(() => {
    const filteredApts = appointments.filter(apt => {
      if (aptDateFrom && apt.date < aptDateFrom) return false;
      if (aptDateTo && apt.date > aptDateTo) return false;
      // Filter by status
      if (aptStatusFilter !== 'todas') {
        if (apt.status !== aptStatusFilter) return false;
      }
      return true;
    });
    const allUsersSet = new Set<string>();
    filteredApts.forEach(apt => allUsersSet.add(apt.createdByUserName || 'Sin registrar'));
    const allAvailableUsers = Array.from(allUsersSet).sort();
    const displayApts = aptSelectedUsers.length > 0 ? filteredApts.filter(apt => aptSelectedUsers.includes(apt.createdByUserName || 'Sin registrar')) : filteredApts;
    const byUser: Record<string, number> = {};
    displayApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      byUser[userName] = (byUser[userName] || 0) + 1;
    });
    const pieData = Object.entries(byUser).map(([name, count], index) => ({
      name,
      value: count,
      color: USER_COLORS[index % USER_COLORS.length]
    })).sort((a, b) => b.value - a.value);
    const getKey = (dateStr: string) => {
      const d = new Date(dateStr + 'T12:00:00');
      if (aptByUserGroupBy === 'day') return dateStr;
      if (aptByUserGroupBy === 'year') return String(d.getFullYear());
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const getLabel = (key: string) => {
      if (aptByUserGroupBy === 'day') {
        const d = new Date(key + 'T12:00:00');
        return d.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short'
        });
      }
      if (aptByUserGroupBy === 'year') return key;
      const [y, m] = key.split('-');
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${monthNames[parseInt(m) - 1]} ${y}`;
    };
    const timeMap: Record<string, Record<string, number>> = {};
    const displayUsers = new Set<string>();
    displayApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      displayUsers.add(userName);
      const key = getKey(apt.date);
      if (!timeMap[key]) timeMap[key] = {};
      timeMap[key][userName] = (timeMap[key][userName] || 0) + 1;
    });
    const timeData = Object.entries(timeMap).sort(([a], [b]) => a.localeCompare(b)).map(([period, users]) => {
      const entry: Record<string, any> = {
        period: getLabel(period)
      };
      displayUsers.forEach(u => {
        entry[u] = users[u] || 0;
      });
      return entry;
    }).slice(-15);
    return {
      pieData,
      timeData,
      userList: Array.from(displayUsers),
      allAvailableUsers,
      totalFiltered: displayApts.length
    };
  }, [appointments, aptByUserGroupBy, aptDateFrom, aptDateTo, aptSelectedUsers, aptStatusFilter]);
  return <div className="space-y-6" data-id="element-2086">
      {/* Filters Card */}
      <Card padding="none" className="overflow-visible relative z-30" data-id="element-2087">
        <div className="h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-2088" />
        <div className="p-4 md:p-6" data-id="element-2089">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4" data-id="element-2090">
            <div className="flex items-center gap-2 min-w-0" data-id="element-2091">
              <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex-shrink-0" data-id="element-2092">
                <UserCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" data-id="element-2093" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-2094">
                Citas Creadas por Usuario
              </h3>
            </div>
            <div className="sm:ml-auto flex items-center gap-2" data-id="element-2095">
              {canDownloadPdf && stats.pieData.length > 0 && <button onClick={() => {
              const cols = [{
                header: 'Usuario',
                key: 'usuario'
              }, {
                header: 'Citas Creadas',
                key: 'citas'
              }];
              const rows = stats.pieData.map(d => ({
                label: d.name,
                values: {
                  usuario: d.name,
                  citas: d.value
                }
              }));
              exportDashboardChartDataToExcel(rows, cols, 'Citas Creadas por Usuario', {
                from: aptDateFrom,
                to: aptDateTo
              });
            }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Descargar Excel" data-id="element-2096">
                  <FileSpreadsheetIcon className="w-4 h-4" data-id="element-2097" />
                </button>}
              {canDownloadPdf && stats.pieData.length > 0 && <button onClick={() => {
              const cols = [{
                header: 'Usuario',
                key: 'usuario'
              }, {
                header: 'Citas Creadas',
                key: 'citas'
              }];
              const rows = stats.pieData.map(d => ({
                label: d.name,
                values: {
                  usuario: d.name,
                  citas: d.value
                }
              }));
              exportDashboardChartDataToPdf(rows, cols, 'Citas Creadas por Usuario', {
                from: aptDateFrom,
                to: aptDateTo
              });
            }} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2098">
                  <DownloadIcon className="w-4 h-4" data-id="element-2099" />
                </button>}
              <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full" data-id="element-2100">
                {stats.totalFiltered} citas
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-id="element-2101">
            <div className="md:col-span-4" data-id="element-2102">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2103">
                Rango de Fecha
              </label>
              <div className="flex items-center gap-2" data-id="element-2104">
                <input type="date" value={aptDateFrom} onChange={e => setAptDateFrom(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-2105" />
                <span className="text-gray-400 font-medium" data-id="element-2106">–</span>
                <input type="date" value={aptDateTo} onChange={e => setAptDateTo(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-2107" />
              </div>
            </div>

            <div className="md:col-span-2" data-id="element-2108">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2109">
                Estado
              </label>
              <Select options={[{
              value: 'completada',
              label: 'Completadas'
            }, {
              value: 'programada',
              label: 'Programadas'
            }, {
              value: 'cancelada',
              label: 'Canceladas'
            }, {
              value: 'reprogramada',
              label: 'Reprogramadas'
            }, {
              value: 'todas',
              label: 'Todas'
            }]} value={aptStatusFilter} onChange={v => setAptStatusFilter(v)} clearable={false} searchable={false} data-id="element-2110" />
            </div>

            <div className="md:col-span-2" data-id="element-2111">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2112">
                Agrupar Por
              </label>
              <Select options={[{
              value: 'day',
              label: 'Día'
            }, {
              value: 'month',
              label: 'Mes'
            }, {
              value: 'year',
              label: 'Año'
            }]} value={aptByUserGroupBy} onChange={v => setAptByUserGroupBy(v as 'day' | 'month' | 'year')} clearable={false} searchable={false} data-id="element-2113" />
            </div>

            <div className="md:col-span-4" ref={aptUserDropdownRef} data-id="element-2114">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2115">
                Usuarios
              </label>
              <div className="relative" data-id="element-2116">
                <div onClick={() => setAptUserDropdownOpen(!aptUserDropdownOpen)} className={`w-full px-3 py-2 rounded-lg border cursor-pointer bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors duration-150 flex items-center justify-between min-h-[42px] ${aptUserDropdownOpen ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2117">
                  <div className="flex-1 flex flex-wrap gap-1 min-w-0" data-id="element-2118">
                    {aptSelectedUsers.length === 0 ? <span className="text-gray-400 dark:text-gray-500 text-sm" data-id="element-2119">
                        Todos los usuarios
                      </span> : aptSelectedUsers.map(u => <span key={u} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium max-w-[120px]" data-id="element-2120">
                          <span className="truncate" data-id="element-2121">{u}</span>
                          <button onClick={e => {
                      e.stopPropagation();
                      setAptSelectedUsers(prev => prev.filter(x => x !== u));
                    }} className="hover:text-indigo-900 dark:hover:text-indigo-100 flex-shrink-0" type="button" data-id="element-2122">
                            <XIcon className="w-3 h-3" data-id="element-2123" />
                          </button>
                        </span>)}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2" data-id="element-2124">
                    {aptSelectedUsers.length > 0 && <button onClick={e => {
                    e.stopPropagation();
                    setAptSelectedUsers([]);
                  }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" type="button" data-id="element-2125">
                        <XIcon className="w-4 h-4 text-gray-400" data-id="element-2126" />
                      </button>}
                    <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform ${aptUserDropdownOpen ? 'rotate-90' : ''}`} data-id="element-2127" />
                  </div>
                </div>

                {aptUserDropdownOpen && <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-2128">
                    {stats.allAvailableUsers.length === 0 ? <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center" data-id="element-2129">
                        No hay usuarios en este rango
                      </div> : <>
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex gap-2" data-id="element-2130">
                          <button onClick={() => setAptSelectedUsers([...stats.allAvailableUsers])} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium" type="button" data-id="element-2131">
                            Seleccionar todos
                          </button>
                          <span className="text-gray-300 dark:text-gray-600" data-id="element-2132">
                            |
                          </span>
                          <button onClick={() => setAptSelectedUsers([])} className="text-xs text-gray-500 dark:text-gray-400 hover:underline font-medium" type="button" data-id="element-2133">
                            Limpiar
                          </button>
                        </div>
                        {stats.allAvailableUsers.map(userName => {
                    const isSelected = aptSelectedUsers.includes(userName);
                    return <div key={userName} onClick={() => setAptSelectedUsers(prev => isSelected ? prev.filter(x => x !== userName) : [...prev, userName])} className={`px-3 py-2.5 cursor-pointer flex items-center gap-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`} data-id="element-2134">
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2135">
                                {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-2136" />}
                              </div>
                              <div className="flex items-center gap-2 min-w-0" data-id="element-2137">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" data-id="element-2138">
                                  {userName.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-900 dark:text-gray-100 truncate" data-id="element-2139">
                                  {userName}
                                </span>
                              </div>
                            </div>;
                  })}
                      </>}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2140">
        <Card padding="none" className="overflow-hidden lg:col-span-2" data-id="element-2141">
          <div className="h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-2142" />
          <div className="p-4 md:p-6" data-id="element-2143">
            <CardHeader className="px-0 pt-0" data-id="element-2144">
              <CardTitle className="flex items-center gap-2 text-base" data-id="element-2145">
                <BarChart3Icon className="w-5 h-5 text-indigo-500" data-id="element-2146" />
                Citas por Período
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2147">
              {stats.timeData.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2148">
                  <UserCheckIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2149" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2150">
                    No hay datos en este rango
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2151">
                    Ajusta las fechas o los usuarios seleccionados
                  </p>
                </div> : <ResponsiveContainer width="100%" height={320} data-id="element-2152">
                  <BarChart data={stats.timeData} data-id="element-2153">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-2154" />
                    <XAxis dataKey="period" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} angle={aptByUserGroupBy === 'day' ? -45 : 0} textAnchor={aptByUserGroupBy === 'day' ? 'end' : 'middle'} height={aptByUserGroupBy === 'day' ? 60 : 30} data-id="element-2155" />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-2156" />
                    <Tooltip contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  padding: '12px 16px'
                }} data-id="element-2157" />
                    <Legend wrapperStyle={{
                  paddingTop: '16px'
                }} data-id="element-2158" />
                    {stats.userList.map((userName, index) => <Bar key={userName} dataKey={userName} name={userName} fill={USER_COLORS[index % USER_COLORS.length]} radius={[4, 4, 0, 0]} stackId="users" data-id="element-2159" />)}
                  </BarChart>
                </ResponsiveContainer>}
            </CardContent>
          </div>
        </Card>

        <Card padding="none" className="overflow-hidden" data-id="element-2160">
          <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500" data-id="element-2161" />
          <div className="p-4 md:p-6" data-id="element-2162">
            <CardHeader className="px-0 pt-0" data-id="element-2163">
              <CardTitle className="flex items-center gap-2 text-base" data-id="element-2164">
                <UsersIcon className="w-5 h-5 text-violet-500" data-id="element-2165" />
                Total por Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2166">
              {stats.pieData.length === 0 ? <div className="text-center py-8 text-gray-400" data-id="element-2167">Sin datos</div> : <>
                  <ResponsiveContainer width="100%" height={200} data-id="element-2168">
                    <PieChart data-id="element-2169">
                      <Pie data={stats.pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" data-id="element-2170">
                        {stats.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2171" />)}
                      </Pie>
                      <Tooltip contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    padding: '8px 12px'
                  }} formatter={(value: number, name: string) => [`${value} citas`, name]} data-id="element-2172" />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-4" data-id="element-2173">
                    {stats.pieData.map((item, index) => <div key={index} className="flex items-center justify-between text-sm" data-id="element-2174">
                        <div className="flex items-center gap-2" data-id="element-2175">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
                      backgroundColor: item.color
                    }} data-id="element-2176" />
                          <span className="text-gray-700 dark:text-gray-300 truncate max-w-[140px]" data-id="element-2177">
                            {item.name}
                          </span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white" data-id="element-2178">
                          {item.value}
                        </span>
                      </div>)}
                  </div>
                </>}
            </CardContent>
          </div>
        </Card>
      </div>
    </div>;
}